import React, { useEffect, useMemo, useState } from 'react';
import {
  Loader2, AlertCircle, Search, X, Download, Package, TrendingDown, AlertTriangle,
  Undo2, ArrowUpDown,
} from 'lucide-react';
import settlementReportService from '../../../services/settlementReportService';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (val) => {
  const n = Number(val ?? 0);
  if (Number.isNaN(n)) return '—';
  const sign = n < 0 ? '-' : '';
  return `${sign}₹${Math.abs(n).toLocaleString('en-IN', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
};
const fmtPct = (val) => (val == null ? '—' : `${Number(val).toFixed(1)}%`);
const fmtInt = (val) => (val == null ? '—' : Number(val).toLocaleString('en-IN'));

// Refund-rate noise threshold: a SKU with 1 unit sold and 1 refund shows 100% refund rate, which
// is meaningless. The "Highest refund rate" insight requires >=2 units sold to keep the bar low
// for small sellers while still filtering out single-sale noise.
const REFUND_RATE_MIN_UNITS = 2;

// ═════════════════════════════════════════════════════════════════════════════

export default function SettlementSkuPerformance({ sellerAccountId, from, to }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | losers | refunded
  const [fulfillment, setFulfillment] = useState('all'); // all | fba | fbm
  const [sortBy, setSortBy] = useState('netProceeds');
  const [sortDir, setSortDir] = useState('desc'); // backend default = net proceeds desc

  useEffect(() => {
    if (!sellerAccountId || !from || !to) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    settlementReportService.getSkuPerformance(sellerAccountId, from, to)
      .then((res) => { if (!cancelled) setRows(Array.isArray(res.data) ? res.data : (res || [])); })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.message || 'Failed to load SKU performance');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sellerAccountId, from, to]);

  // Period totals strip — current-period totals only (no prior-period delta in v1).
  const totals = useMemo(() => {
    return rows.reduce((acc, r) => ({
      unitsSold:    acc.unitsSold + (Number(r.unitsSold) || 0),
      grossSales:   acc.grossSales + (Number(r.grossSales) || 0),
      refundAmount: acc.refundAmount + (Number(r.refundAmount) || 0),
      amazonFees:   acc.amazonFees + (Number(r.amazonFees) || 0),
      netProceeds:  acc.netProceeds + (Number(r.netProceeds) || 0),
      skuCount:     acc.skuCount + 1,
    }), { unitsSold: 0, grossSales: 0, refundAmount: 0, amazonFees: 0, netProceeds: 0, skuCount: 0 });
  }, [rows]);

  // Insight cards.
  // Loss-makers are ranked by `orderNet` — refund-free per-sale economics. A SKU with great
  // unit economics that happened to get one refund this period shouldn't be flagged.
  const topLosers = useMemo(
    () => [...rows]
      .filter(r => Number(r.orderNet) < 0)
      .sort((a, b) => Number(a.orderNet) - Number(b.orderNet))
      .slice(0, 5),
    [rows]
  );
  // Use refundAmount > 0 as the trigger (not refundRatePct > 0): rate may be zero if Refund
  // rows lack quantity, but we always have an amount.
  const topRefundRate = useMemo(
    () => [...rows]
      .filter(r => Number(r.unitsSold) >= REFUND_RATE_MIN_UNITS && Number(r.refundAmount) > 0)
      .sort((a, b) => Number(b.refundRatePct) - Number(a.refundRatePct))
      .slice(0, 5),
    [rows]
  );

  const totalRefundAmount = totals.refundAmount;

  const filtered = useMemo(() => {
    let out = rows;
    if (filter === 'losers') out = out.filter(r => Number(r.orderNet) < 0);
    else if (filter === 'refunded') out = out.filter(r => Number(r.refundAmount) > 0);

    if (fulfillment === 'fba') out = out.filter(r => r.fulfillment === 'AFN');
    else if (fulfillment === 'fbm') out = out.filter(r => r.fulfillment === 'MFN');

    if (search) {
      const q = search.toLowerCase();
      out = out.filter(r =>
        r.sku?.toLowerCase().includes(q) ||
        r.title?.toLowerCase().includes(q) ||
        r.asin?.toLowerCase().includes(q) ||
        r.brand?.toLowerCase().includes(q)
      );
    }

    out = [...out].sort((a, b) => {
      const va = Number(a[sortBy]) || 0;
      const vb = Number(b[sortBy]) || 0;
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return out;
  }, [rows, filter, fulfillment, search, sortBy, sortDir]);

  const handleSort = (field) => {
    if (sortBy === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('desc'); }
  };

  const handleExportCsv = () => {
    if (filtered.length === 0) return;
    const headers = [
      'SKU', 'ASIN', 'Title', 'Brand', 'Fulfilment',
      'Units Sold', 'Gross Sales', 'Refund Amount', 'Refund Units', 'Refund Rate %',
      'Amazon Fees', 'Selling Fees', 'FBA Fees', 'Other Fees', 'Tax Withheld',
      'Net Proceeds', 'Margin %',
    ];
    const esc = (v) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const num = (v) => (v == null || v === '' ? 0 : Number(v));
    const csvRows = filtered.map(r => [
      esc(r.sku), esc(r.asin), esc(r.title), esc(r.brand), esc(r.fulfillment),
      num(r.unitsSold), num(r.grossSales), num(r.refundAmount), num(r.refundUnits), num(r.refundRatePct),
      num(r.amazonFees), num(r.sellingFees), num(r.fbaFees), num(r.otherFees), num(r.taxWithheld),
      num(r.netProceeds), num(r.marginPct),
    ].join(','));
    const csv = [headers.join(','), ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sku-performance_${from}_to_${to}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="p-10 flex items-center justify-center text-slate-400">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-8 text-center">
        <AlertCircle size={28} className="mx-auto text-rose-500 mb-2" />
        <p className="text-sm text-rose-700">{error}</p>
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="p-10 text-center">
        <Package size={28} className="mx-auto text-slate-300 mb-2" />
        <p className="text-sm font-semibold text-slate-700">No SKU activity in this date range</p>
        <p className="text-xs text-slate-500 mt-1">Try a different range or sync new settlements.</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-5 space-y-3">
      {/* ─── Period totals strip ─────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <Stat label="SKUs" value={fmtInt(totals.skuCount)} />
        <Stat label="Units sold" value={fmtInt(totals.unitsSold)} />
        <Stat label="Gross sales" value={fmt(totals.grossSales)} />
        <Stat label="Refunds" value={fmt(totals.refundAmount)} tone="rose" />
        <Stat label="Amazon fees" value={fmt(totals.amazonFees)} tone="rose" />
        <Stat label="Net proceeds" value={fmt(totals.netProceeds)}
          tone={totals.netProceeds < 0 ? 'rose' : 'emerald'} />
      </div>

      {/* ─── Insight cards ───────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <InsightCard
          icon={TrendingDown}
          tone="rose"
          title="Top 5 loss-makers"
          subtitle="SKUs whose unit economics lose money (fees > revenue, refund-free)"
          empty="No loss-makers in this period 🎉"
          rows={topLosers}
          metricLabel="Net / sale"
          metricFn={(r) => fmt(r.orderNet)}
          metricNegative={(r) => Number(r.orderNet) < 0}
        />
        <InsightCard
          icon={AlertTriangle}
          tone="amber"
          title="Highest refund rate"
          subtitle={`SKUs with ≥${REFUND_RATE_MIN_UNITS} units sold, sorted by refund %`}
          empty={
            totalRefundAmount > 0
              ? 'Refunds in this period have low unit counts — increase the date range to see SKU-level rates'
              : 'No refunds in this period 🎉'
          }
          rows={topRefundRate}
          metricLabel="Refund %"
          metricFn={(r) => fmtPct(r.refundRatePct)}
          metricNegative={(r) => Number(r.refundRatePct) > 10}
        />
      </div>

      {/* ─── Filter bar ─────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search SKU, ASIN, title…"
            className="pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804] w-[240px]"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X size={12} />
            </button>
          )}
        </div>

        <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>
          All ({rows.length})
        </FilterChip>
        <FilterChip active={filter === 'losers'} onClick={() => setFilter('losers')}>
          Loss-makers ({rows.filter(r => Number(r.orderNet) < 0).length})
        </FilterChip>
        <FilterChip active={filter === 'refunded'} onClick={() => setFilter('refunded')}>
          With refunds ({rows.filter(r => Number(r.refundAmount) > 0).length})
        </FilterChip>

        <span className="w-px h-5 bg-slate-200 mx-1" />

        <FilterChip active={fulfillment === 'all'} onClick={() => setFulfillment('all')}>
          All Fulfilment
        </FilterChip>
        <FilterChip active={fulfillment === 'fba'} onClick={() => setFulfillment('fba')}>
          FBA ({rows.filter(r => r.fulfillment === 'AFN').length})
        </FilterChip>
        <FilterChip active={fulfillment === 'fbm'} onClick={() => setFulfillment('fbm')}>
          FBM ({rows.filter(r => r.fulfillment === 'MFN').length})
        </FilterChip>

        <div className="flex-1" />

        <button
          onClick={handleExportCsv}
          disabled={filtered.length === 0}
          className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium bg-white border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 disabled:opacity-50"
        >
          <Download size={11} /> Export CSV
        </button>
      </div>

      {/* ─── Table ───────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              <tr>
                <Th>Product</Th>
                <Th right sortable sortBy={sortBy} sortDir={sortDir} field="unitsSold" onSort={handleSort}>Units</Th>
                <Th right sortable sortBy={sortBy} sortDir={sortDir} field="grossSales" onSort={handleSort}>Gross sales</Th>
                <Th right sortable sortBy={sortBy} sortDir={sortDir} field="refundAmount" onSort={handleSort}>Refunds</Th>
                <Th right sortable sortBy={sortBy} sortDir={sortDir} field="amazonFees" onSort={handleSort}>Amazon fees</Th>
                <Th right sortable sortBy={sortBy} sortDir={sortDir} field="netProceeds" onSort={handleSort}>Net proceeds</Th>
                <Th right sortable sortBy={sortBy} sortDir={sortDir} field="marginPct" onSort={handleSort}>Margin %</Th>
                <Th right sortable sortBy={sortBy} sortDir={sortDir} field="refundRatePct" onSort={handleSort}>Refund rate</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(r => {
                const negativeNet = Number(r.netProceeds) < 0;
                const highRefund = Number(r.refundRatePct) > 10 && Number(r.unitsSold) >= REFUND_RATE_MIN_UNITS;
                return (
                  <tr key={r.sku} className="hover:bg-slate-50/40">
                    <td className="px-3 py-2 max-w-[320px]">
                      <div className="flex items-center gap-2">
                        {r.imageUrl ? (
                          <img src={r.imageUrl} alt="" className="w-8 h-8 rounded border border-slate-200 object-contain bg-white flex-shrink-0" />
                        ) : (
                          <div className="w-8 h-8 rounded border border-slate-200 bg-slate-50 flex items-center justify-center flex-shrink-0">
                            <Package size={12} className="text-slate-300" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="text-[11px] font-semibold text-slate-700 truncate" title={r.title || r.sku}>
                            {r.title || r.sku}
                          </p>
                          <p className="text-[10px] text-slate-500 font-mono truncate">
                            {r.sku}{r.asin && <> · {r.asin}</>}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">{fmtInt(r.unitsSold)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">{fmt(r.grossSales)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {Number(r.refundAmount) > 0 ? (
                        <span className="text-rose-600">
                          {fmt(r.refundAmount)}
                          {Number(r.refundUnits) > 0 && (
                            <span className="text-[10px] text-slate-400 ml-1">({r.refundUnits}u)</span>
                          )}
                        </span>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-rose-600">{fmt(r.amazonFees)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums font-semibold ${
                      negativeNet ? 'text-rose-700' : 'text-emerald-700'
                    }`}>
                      {fmt(r.netProceeds)}
                    </td>
                    <td className={`px-3 py-2 text-right tabular-nums ${marginTone(r.marginPct)}`}>
                      {fmtPct(r.marginPct)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.refundRatePct == null ? (
                        <span className="text-slate-300">—</span>
                      ) : highRefund ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-50 border border-amber-200 text-amber-700">
                          {fmtPct(r.refundRatePct)}
                        </span>
                      ) : (
                        <span className="text-slate-600">{fmtPct(r.refundRatePct)}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-slate-50 border-t-2 border-slate-200 text-xs font-bold text-slate-700">
              <tr>
                <td className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500">
                  Totals ({filtered.length} SKU{filtered.length === 1 ? '' : 's'})
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtInt(filtered.reduce((s, r) => s + (Number(r.unitsSold) || 0), 0))}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmt(filtered.reduce((s, r) => s + (Number(r.grossSales) || 0), 0))}</td>
                <td className="px-3 py-2 text-right tabular-nums text-rose-600">{fmt(filtered.reduce((s, r) => s + (Number(r.refundAmount) || 0), 0))}</td>
                <td className="px-3 py-2 text-right tabular-nums text-rose-600">{fmt(filtered.reduce((s, r) => s + (Number(r.amazonFees) || 0), 0))}</td>
                <td className={`px-3 py-2 text-right tabular-nums ${
                  filtered.reduce((s, r) => s + (Number(r.netProceeds) || 0), 0) < 0 ? 'text-rose-700' : 'text-emerald-700'
                }`}>
                  {fmt(filtered.reduce((s, r) => s + (Number(r.netProceeds) || 0), 0))}
                </td>
                <td className="px-3 py-2"></td>
                <td className="px-3 py-2"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function Stat({ label, value, tone }) {
  const valueCls =
    tone === 'rose' ? 'text-rose-700' :
    tone === 'emerald' ? 'text-emerald-700' :
    'text-slate-900';
  return (
    <div className="bg-white rounded-lg border border-slate-200 px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`text-sm font-bold tabular-nums mt-0.5 ${valueCls}`}>{value}</p>
    </div>
  );
}

function InsightCard({ icon: Icon, tone, title, subtitle, empty, rows, metricLabel, metricFn, metricNegative }) {
  const palette = {
    rose:  { head: 'text-rose-700',  ring: 'border-rose-200',  bg: 'bg-rose-50/40' },
    amber: { head: 'text-amber-700', ring: 'border-amber-200', bg: 'bg-amber-50/40' },
  }[tone] || { head: 'text-slate-700', ring: 'border-slate-200', bg: 'bg-white' };

  return (
    <div className={`rounded-xl border ${palette.ring} ${palette.bg} p-3`}>
      <div className="flex items-start gap-2 mb-2">
        <Icon size={14} className={palette.head} />
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-bold ${palette.head}`}>{title}</p>
          <p className="text-[10px] text-slate-500">{subtitle}</p>
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="text-[11px] text-slate-400 italic px-1 py-2">{empty}</p>
      ) : (
        <ul className="space-y-1">
          {rows.map((r) => (
            <li key={r.sku} className="flex items-center gap-2 bg-white border border-slate-200 rounded px-2 py-1 text-[11px]">
              <span className="font-mono text-[10px] text-slate-500 flex-shrink-0">{r.sku}</span>
              <span className="text-slate-700 truncate flex-1" title={r.title || r.sku}>
                {r.title || '—'}
              </span>
              <span className="text-[10px] text-slate-400 tabular-nums flex-shrink-0">{metricLabel}</span>
              <span className={`tabular-nums font-semibold flex-shrink-0 ${
                metricNegative(r) ? 'text-rose-700' : 'text-slate-700'
              }`}>{metricFn(r)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold border transition-colors whitespace-nowrap ${
        active
          ? 'bg-[#FFF4E6] border-[#f08804] text-[#C45500]'
          : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
      }`}
    >
      {children}
    </button>
  );
}

function Th({ children, right, sortable, sortBy, sortDir, field, onSort }) {
  const cls = `px-3 py-2 ${right ? 'text-right' : 'text-left'} ${sortable ? 'cursor-pointer hover:text-slate-700 select-none' : ''}`;
  if (!sortable) return <th className={cls}>{children}</th>;
  return (
    <th className={cls} onClick={() => onSort(field)}>
      <span className={`inline-flex items-center gap-0.5 ${right ? 'justify-end w-full' : ''}`}>
        {children}
        {sortBy === field
          ? <span className="text-[#C45500]">{sortDir === 'asc' ? '▲' : '▼'}</span>
          : <ArrowUpDown size={9} className="text-slate-300" />}
      </span>
    </th>
  );
}

function marginTone(pct) {
  const n = Number(pct);
  if (Number.isNaN(n) || pct == null) return 'text-slate-500';
  if (n >= 30) return 'text-emerald-700 font-semibold';
  if (n >= 10) return 'text-amber-600';
  if (n >= 0) return 'text-rose-600';
  return 'text-rose-700 font-bold';
}
